package br.com.htms.inspeccaocampo.data.efetivo

import br.com.htms.inspeccaocampo.auth.AuthApi
import br.com.htms.inspeccaocampo.data.JsonMap
import kotlin.math.floor

/** Datas ISO (aaaa-mm-dd) em dias corridos, sem fuso. */
object Datas {
    private val MESES = listOf("jan", "fev", "mar", "abr", "mai", "jun", "jul", "ago", "set", "out", "nov", "dez")
    val DIAS_SEMANA = listOf("dom", "seg", "ter", "qua", "qui", "sex", "sáb")

    fun dn(iso: String): Long {
        val p = iso.split("-")
        if (p.size != 3) return 0
        var y = p[0].toLongOrNull() ?: return 0
        val m = p[1].toLongOrNull() ?: return 0
        val d = p[2].toLongOrNull() ?: return 0
        if (m <= 2) y -= 1
        val era = (if (y >= 0) y else y - 399) / 400
        val yoe = y - era * 400
        val doy = (153 * (m + (if (m > 2) -3 else 9)) + 2) / 5 + d - 1
        val doe = yoe * 365 + yoe / 4 - yoe / 100 + doy
        return era * 146097 + doe - 719468
    }

    fun ds(n: Long): String {
        val z = n + 719468
        val era = (if (z >= 0) z else z - 146096) / 146097
        val doe = z - era * 146097
        val yoe = (doe - doe / 1460 + doe / 36524 - doe / 146096) / 365
        val doy = doe - (365 * yoe + yoe / 4 - yoe / 100)
        val mp = (5 * doy + 2) / 153
        val d = doy - (153 * mp + 2) / 5 + 1
        val m = mp + (if (mp < 10) 3 else -9)
        val y = yoe + era * 400 + (if (m <= 2) 1 else 0)
        return "$y-${m.toString().padStart(2, '0')}-${d.toString().padStart(2, '0')}"
    }

    fun valida(iso: String): Boolean = Regex("""\d{4}-\d{2}-\d{2}""").matches(iso) && ds(dn(iso)) == iso
    fun add(iso: String, k: Int): String = ds(dn(iso) + k)
    fun diff(a: String, b: String): Int = (dn(a) - dn(b)).toInt()

    /** 0 = domingo. */
    fun dow(iso: String): Int = (((dn(iso) % 7) + 7 + 4) % 7).toInt()
    fun fimDeSemana(iso: String): Boolean = dow(iso).let { it == 0 || it == 6 }

    fun dias(a: String, b: String): List<String> {
        if (a > b) return emptyList()
        return (dn(a)..dn(b)).map(::ds)
    }

    fun mesRange(comp: String): Pair<String, String> {
        val (y, m) = comp.split("-").map { it.toInt() }
        val prox = if (m == 12) "${y + 1}-01-01" else "$y-${(m + 1).toString().padStart(2, '0')}-01"
        return "$comp-01" to add(prox, -1)
    }

    fun compAnterior(comp: String): String {
        val (y, m) = comp.split("-").map { it.toInt() }
        return if (m == 1) "${y - 1}-12" else "$y-${(m - 1).toString().padStart(2, '0')}"
    }

    fun compSeguinte(comp: String): String {
        val (y, m) = comp.split("-").map { it.toInt() }
        return if (m == 12) "${y + 1}-01" else "$y-${(m + 1).toString().padStart(2, '0')}"
    }

    fun mesRotulo(comp: String): String {
        val p = comp.split("-")
        val m = p.getOrNull(1)?.toIntOrNull() ?: return comp
        return "${MESES[(m - 1).coerceIn(0, 11)]}/${p[0]}"
    }

    fun br(iso: String?): String {
        if (iso.isNullOrBlank()) return ""
        val p = iso.split("-")
        return if (p.size == 3) "${p[2]}/${p[1]}/${p[0]}" else iso
    }

    /** dd/mm/aaaa → ISO; null se inválida. */
    fun iso(br: String): String? {
        val m = Regex("""(\d{1,2})/(\d{1,2})/(\d{4})""").matchEntire(br.trim()) ?: return null
        val v = "${m.groupValues[3]}-${m.groupValues[2].padStart(2, '0')}-${m.groupValues[1].padStart(2, '0')}"
        return v.takeIf(::valida)
    }
}

object TiposDia {
    const val TRABALHO = "trabalho"
    val todos: List<Pair<String, String>> = listOf(
        TRABALHO to "Trabalho",
        "folga" to "Folga",
        "descanso_semanal" to "Descanso semanal",
        "ferias" to "Férias",
        "afastamento" to "Afastamento",
        "falta" to "Falta",
        "feriado" to "Feriado"
    )
    val letra: Map<String, String> = mapOf(
        "folga" to "F", "descanso_semanal" to "D", "ferias" to "V",
        "afastamento" to "A", "falta" to "X", "feriado" to "H"
    )

    fun rotulo(t: String): String = todos.firstOrNull { it.first == t }?.second ?: t
}

data class CentroEfetivo(
    val id: String,
    val codigo: String,
    val nome: String,
    val ativo: Boolean,
    val engenheiro: String,
    val engenheiroNome: String
) {
    val rotulo: String get() = "$codigo · $nome"
}

data class AlocPeriodo(val id: String, val ini: String, val fim: String?, val cc: String)

data class FuncEfetivo(
    val id: String,
    val nome: String,
    val matricula: String,
    val funcao: String,
    val vinculo: String,
    val situacao: String,
    val admissao: String,
    val regime: Int,
    val desligamento: String?,
    val aloc: List<AlocPeriodo>,
    val dias: Map<String, String>
) {
    val funcaoOuSem: String get() = funcao.ifBlank { "Sem função" }

    fun afastadoCadastro(): Boolean = situacao.trim().uppercase().startsWith("AFAST")

    fun desativadoCadastro(): Boolean = situacao.trim().uppercase().startsWith("DESATIV")

    /** Ainda no emprego nessa data: não desativado, já admitido e sem desligamento anterior. */
    fun ativoEm(data: String): Boolean {
        if (desativadoCadastro()) return false
        return (admissao.isBlank() || admissao <= data) && (desligamento == null || data <= desligamento)
    }

    /** Mesma conta de "Ativos" do módulo Colaboradores: situação ATIVO (ou em branco), na data. */
    fun noQuadro(data: String): Boolean {
        if (!ativoEm(data) || afastadoCadastro()) return false
        val s = situacao.trim().uppercase()
        return s.isBlank() || s.startsWith("ATIVO")
    }
}

data class AprovLado(
    val obra: String,
    val dec: String,
    val por: String,
    val porNome: String,
    val em: Long?,
    val auto: Boolean,
    val com: String
)

data class Transferencia(
    val id: String,
    val fid: String,
    val origem: String,
    val destino: String,
    val ini: String,
    val fim: String?,
    val motivo: String,
    val solicitante: String,
    val solicitanteNome: String,
    val status: String,
    val criada: Long,
    val prazo: Long,
    val avisoDiretoria: Long?,
    val aplicadaEm: Long?,
    val origemLado: AprovLado,
    val destinoLado: AprovLado
) {
    val aberta: Boolean get() = status == "pendente" || status == "parcial"
    fun vencida(agora: Long): Boolean = aberta && agora > prazo
    fun lado(chave: String): AprovLado = if (chave == "destino") destinoLado else origemLado
}

data class Previsto(val cc: String, val funcao: String, val qtd: Int)

data class EfetivoDados(
    val hoje: String,
    val master: Boolean,
    val usuario: String,
    val custoAtivo: Boolean,
    val centros: List<CentroEfetivo>,
    val ccsEngenheiro: List<String>,
    val funcionarios: List<FuncEfetivo>,
    val transferencias: List<Transferencia>,
    val previsto: List<Previsto>,
    val funcoes: List<String>,
    val podeEditar: Boolean = true,
    val podeExcluir: Boolean = false
) {
    fun centro(id: String?): CentroEfetivo? = centros.firstOrNull { it.id == id }
    fun codigo(id: String?): String = if (id == null) "Sem obra" else centro(id)?.codigo ?: "—"
    fun rotulo(id: String?): String = if (id == null) "Sem obra" else centro(id)?.rotulo ?: "—"
    fun func(id: String): FuncEfetivo? = funcionarios.firstOrNull { it.id == id }
    fun podeDecidir(cc: String): Boolean = master || cc in ccsEngenheiro
}

data class EfetivoLog(
    val id: String,
    val createdAt: Long,
    val usuario: String,
    val nome: String,
    val acao: String,
    val entidade: String,
    val txt: String,
    val retro: Int
)

data class CustoMes(
    val comp: String,
    val versao: Int,
    val valores: Map<String, Long>,
    val total: Long,
    val importadoPor: String,
    val em: Long,
    val naoCasadas: List<String>
)

data class FolgaStatus(
    val ultima: String,
    val dias: Int,
    val limite: String,
    val adiada: Boolean,
    val janela: Pair<String, String>?,
    val status: String
) {
    companion object {
        const val EM_DIA = "em_dia"
        const val PROXIMA = "proxima"
        const val ADIADA = "adiada"
        const val ATRASADA = "atrasada"
        const val SEM_DATA = "sem_data"
    }
}

data class LinhaRateio(val fid: String, val obra: String, val dias: Int, val cents: Long, val provisorio: Boolean)

data class ImportacaoFolha(
    val erro: String? = null,
    val comp: String = "",
    val ok: List<Pair<String, Long>> = emptyList(),
    val naoCasadas: List<Pair<Int, String>> = emptyList(),
    val invalidas: List<Pair<Int, String>> = emptyList()
) {
    val total: Long get() = ok.sumOf { it.second }
}

/** Regras do modelo "Controle do efetivo" (funções puras). */
object RegrasEfetivo {
    const val SEM_OBRA = "_sem"
    private val ZERAM = setOf("folga", "ferias", "afastamento")

    fun obraEm(aloc: List<AlocPeriodo>, data: String): String? =
        aloc.firstOrNull { it.ini <= data && (it.fim == null || data <= it.fim) }?.cc

    /** Obra do cadastro: a vigente na data ou a próxima já definida (admissão ainda à frente). */
    fun obraCadastrada(aloc: List<AlocPeriodo>, data: String): String? =
        obraEm(aloc, data) ?: aloc.filter { it.ini > data }.minByOrNull { it.ini }?.cc

    fun tipoDia(f: FuncEfetivo, data: String): String = f.dias[data] ?: TiposDia.TRABALHO

    /** Dias de vínculo do funcionário dentro do mês [comp]. */
    fun vinculo(f: FuncEfetivo, comp: String): Pair<String, String>? {
        val (a, b) = Datas.mesRange(comp)
        val ini = if (f.admissao.isNotBlank() && f.admissao > a) f.admissao else a
        val fim = if (f.desligamento != null && f.desligamento < b) f.desligamento else b
        return if (ini <= fim) ini to fim else null
    }

    /**
     * Dias corridos desde a última folga, férias ou afastamento (ou admissão). Descanso semanal não zera.
     * Limite caindo a partir de 15/10 é adiado para a janela 23/12 a 05/01.
     */
    fun folgaStatus(f: FuncEfetivo, ref: String): FolgaStatus {
        var ultima = f.admissao.ifBlank { f.aloc.firstOrNull()?.ini.orEmpty() }
        for ((d, t) in f.dias) if (t in ZERAM && d <= ref && (ultima.isBlank() || d > ultima)) ultima = d
        if (ultima.isBlank()) return FolgaStatus("", 0, "", false, null, FolgaStatus.SEM_DATA)
        val dias = Datas.diff(ref, ultima)
        val limite = Datas.add(ultima, f.regime)
        val ano = limite.take(4).toInt()
        val adiada = limite >= "$ano-10-15"
        val janela = "$ano-12-23" to "${ano + 1}-01-05"
        val status = when {
            dias < f.regime - 7 -> FolgaStatus.EM_DIA
            dias < f.regime -> FolgaStatus.PROXIMA
            adiada && ref <= janela.second -> FolgaStatus.ADIADA
            else -> FolgaStatus.ATRASADA
        }
        return FolgaStatus(ultima, dias, limite, adiada, if (adiada) janela else null, status)
    }

    fun fimDeSemanaTrabalhado(f: FuncEfetivo, comp: String): Int {
        val v = vinculo(f, comp) ?: return 0
        return Datas.dias(v.first, v.second).count { Datas.fimDeSemana(it) && tipoDia(f, it) == TiposDia.TRABALHO }
    }

    /** Distribui [cents] entre as obras pelo método do maior resto, fechando o total exato. */
    fun distribuir(cents: Long, counts: Map<String, Int>): Map<String, Long> {
        val obras = counts.keys.sorted()
        val total = obras.sumOf { counts.getValue(it) }
        if (total == 0) return emptyMap()
        val base = mutableMapOf<String, Long>()
        val resto = mutableListOf<Pair<Double, String>>()
        var usado = 0L
        for (o in obras) {
            val exato = cents.toDouble() * counts.getValue(o) / total
            val b = floor(exato).toLong()
            base[o] = b
            usado += b
            resto += (exato - b) to o
        }
        resto.sortWith(compareByDescending<Pair<Double, String>> { it.first }.thenBy { it.second })
        var i = 0
        while (usado < cents) {
            val o = resto[i % resto.size].second
            base[o] = base.getValue(o) + 1
            usado++
            i++
        }
        return base
    }

    /** Valor real do mês ou, sem folha importada, o último valor conhecido (provisório). */
    fun custoDe(custos: List<CustoMes>, fid: String, comp: String): Pair<Long, Boolean>? {
        custos.firstOrNull { it.comp == comp }?.valores?.get(fid)?.let { return it to false }
        return custos.filter { it.comp < comp && it.valores[fid] != null }.maxByOrNull { it.comp }
            ?.let { it.valores.getValue(fid) to true }
    }

    fun rateio(funcs: List<FuncEfetivo>, custos: List<CustoMes>, comp: String): Pair<List<LinhaRateio>, List<String>> {
        val linhas = mutableListOf<LinhaRateio>()
        val semCusto = mutableListOf<String>()
        for (f in funcs) {
            val v = vinculo(f, comp) ?: continue
            val c = custoDe(custos, f.id, comp)
            if (c == null) {
                semCusto += f.id
                continue
            }
            val counts = mutableMapOf<String, Int>()
            for (d in Datas.dias(v.first, v.second)) {
                val o = obraEm(f.aloc, d) ?: SEM_OBRA
                counts[o] = (counts[o] ?: 0) + 1
            }
            val parte = distribuir(c.first, counts)
            for ((o, n) in counts) linhas += LinhaRateio(f.id, o, n, parte[o] ?: 0, c.second)
        }
        return linhas to semCusto
    }

    private fun norm(s: String): String = s.trim().lowercase()
        .replace('á', 'a').replace('â', 'a').replace('ã', 'a').replace('à', 'a')
        .replace('é', 'e').replace('ê', 'e').replace('í', 'i')
        .replace('ó', 'o').replace('ô', 'o').replace('õ', 'o').replace('ú', 'u').replace('ç', 'c')

    fun parseValor(s: String): Long? {
        var t = s.replace("R$", "", ignoreCase = true).filterNot { it.isWhitespace() }
        if (t.contains(',') && t.contains('.')) t = t.replace(".", "").replace(',', '.')
        else if (t.contains(',')) t = t.replace(',', '.')
        if (!Regex("""-?\d+(\.\d+)?""").matches(t)) return null
        return kotlin.math.round(t.toDouble() * 100).toLong()
    }

    fun parseComp(s: String): String? {
        val t = s.trim()
        Regex("""(\d{4})-(\d{2})""").matchEntire(t)?.let { return t }
        Regex("""(\d{2})/(\d{4})""").matchEntire(t)?.let { return "${it.groupValues[2]}-${it.groupValues[1]}" }
        return null
    }

    /** Planilha do DP com as colunas matricula; competencia; valor. */
    fun parseImport(text: String, funcs: List<FuncEfetivo>): ImportacaoFolha {
        val lines = text.lines().filter { it.isNotBlank() }
        if (lines.isEmpty()) return ImportacaoFolha(erro = "Arquivo vazio.")
        val sep = when {
            lines[0].contains(';') -> ";"
            lines[0].contains('\t') -> "\t"
            else -> ","
        }
        val head = lines[0].split(sep).map(::norm)
        val iM = head.indexOf("matricula")
        val iC = head.indexOf("competencia")
        val iV = head.indexOfFirst { it == "valor" || it == "custo" }
        if (iM < 0 || iC < 0 || iV < 0) {
            return ImportacaoFolha(erro = "Cabeçalho inválido. Use as colunas: matricula; competencia; valor.")
        }
        val porMat = funcs.filter { it.matricula.isNotBlank() }.associateBy { it.matricula.trim() }
        val ok = mutableListOf<Pair<String, Long>>()
        val nao = mutableListOf<Pair<Int, String>>()
        val inv = mutableListOf<Pair<Int, String>>()
        val vistos = mutableSetOf<String>()
        var comp: String? = null
        lines.drop(1).forEachIndexed { i, l ->
            val n = i + 2
            val c = l.split(sep)
            val mat = c.getOrNull(iM)?.trim().orEmpty()
            val cp = parseComp(c.getOrNull(iC).orEmpty())
            val v = parseValor(c.getOrNull(iV).orEmpty())
            when {
                mat.isEmpty() || cp == null || v == null || v < 0 -> inv += n to "Matrícula, competência ou valor inválido."
                comp != null && cp != comp -> inv += n to "Competência diferente das demais linhas."
                mat in vistos -> inv += n to "Matrícula repetida no arquivo."
                else -> {
                    comp = comp ?: cp
                    vistos += mat
                    val f = porMat[mat]
                    if (f == null) nao += n to mat else ok += f.id to v
                }
            }
        }
        return ImportacaoFolha(comp = comp.orEmpty(), ok = ok, naoCasadas = nao, invalidas = inv)
    }
}

/** Cliente das rotas `/efetivo/...` do Worker. */
object EfetivoApi {
    suspend fun dados(): Result<EfetivoDados> =
        AuthApi.apiJson("GET", "/efetivo/dados").mapCatching { text ->
            val o = JsonMap.parse(text)
            EfetivoDados(
                hoje = o.str("hoje"),
                master = o.bool("master"),
                usuario = o.str("usuario"),
                custoAtivo = o.bool("custoAtivo"),
                centros = objs(o, "centros").map {
                    CentroEfetivo(
                        it.str("id"), it.str("codigo"), it.str("nome"), it.bool("ativo", true),
                        it.str("engenheiro"), it.str("engenheiroNome")
                    )
                },
                ccsEngenheiro = o.strList("ccsEngenheiro"),
                funcionarios = objs(o, "funcionarios").map { f ->
                    FuncEfetivo(
                        id = f.str("id"),
                        nome = f.str("nome"),
                        matricula = f.str("matricula"),
                        funcao = f.str("funcao"),
                        vinculo = f.str("vinculo"),
                        situacao = f.str("situacao"),
                        admissao = f.str("admissao"),
                        regime = (f.long("regime") ?: 60L).toInt(),
                        desligamento = f.str("desligamento").ifBlank { null },
                        aloc = objs(f, "aloc").map { AlocPeriodo(it.str("id"), it.str("ini"), it.str("fim").ifBlank { null }, it.str("cc")) },
                        dias = f.obj("dias")?.stringValues().orEmpty()
                    )
                },
                transferencias = objs(o, "transferencias").map(::transfFrom),
                previsto = objs(o, "previsto").map { Previsto(it.str("cc"), it.str("funcao"), (it.long("qtd") ?: 0L).toInt()) },
                funcoes = o.strList("funcoes"),
                podeEditar = o.bool("podeEditar", true),
                podeExcluir = o.bool("podeExcluir")
            )
        }

    suspend fun log(): Result<List<EfetivoLog>> =
        AuthApi.apiJson("GET", "/efetivo/log").mapCatching { text ->
            objs(JsonMap.parse(text), "logs").map {
                EfetivoLog(
                    it.str("id"), it.long("createdAt") ?: 0L, it.str("usuario"), it.str("nome"),
                    it.str("acao"), it.str("entidade"), it.str("txt"), (it.long("retro") ?: 0L).toInt()
                )
            }
        }

    /** Retorna quantos foram alocados e os recusados (nome e motivo). */
    suspend fun alocar(fids: List<String>, cc: String, ini: String, trocar: Boolean = false): Result<Pair<Int, List<String>>> =
        AuthApi.apiJson(
            "POST", "/efetivo/alocar",
            "{\"fids\":[${fids.joinToString(",") { jsonStr(it) }}],\"cc\":${jsonStr(cc)},\"ini\":${jsonStr(ini)},\"trocar\":$trocar}"
        ).mapCatching { text ->
            val o = JsonMap.parse(text)
            (o.long("alocados") ?: 0L).toInt() to objs(o, "recusados").map { "${it.str("nome").ifBlank { it.str("id") }}: ${it.str("motivo")}" }
        }

    suspend fun removerAlocacao(id: String): Result<Unit> =
        AuthApi.apiJson("DELETE", "/efetivo/alocacoes/${enc(id)}", "{}").map { }

    suspend fun lancar(fid: String, de: String, ate: String, tipo: String): Result<Int> =
        AuthApi.apiJson(
            "POST", "/efetivo/lancar",
            "{\"fid\":${jsonStr(fid)},\"de\":${jsonStr(de)},\"ate\":${jsonStr(ate)},\"tipo\":${jsonStr(tipo)}}"
        ).mapCatching { (JsonMap.parse(it).long("dias") ?: 0L).toInt() }

    /** true se a transferência já foi aplicada (solicitante aprovou os dois lados). */
    suspend fun solicitarTransferencia(
        fid: String, origem: String, destino: String, ini: String, fim: String, motivo: String
    ): Result<Boolean> =
        AuthApi.apiJson(
            "POST", "/efetivo/transferencias",
            "{\"fid\":${jsonStr(fid)},\"origem\":${jsonStr(origem)},\"destino\":${jsonStr(destino)}," +
                "\"ini\":${jsonStr(ini)},\"fim\":${jsonStr(fim)},\"motivo\":${jsonStr(motivo)}}"
        ).mapCatching { JsonMap.parse(it).bool("aplicada") }

    suspend fun acaoTransferencia(id: String, acao: String, lado: String = "", comentario: String = ""): Result<Boolean> =
        AuthApi.apiJson(
            "POST", "/efetivo/transferencias/${enc(id)}/acao",
            "{\"acao\":${jsonStr(acao)},\"lado\":${jsonStr(lado)},\"comentario\":${jsonStr(comentario)}}"
        ).mapCatching { JsonMap.parse(it).bool("aplicada") }

    suspend fun salvarPrevisto(cc: String, funcao: String, qtd: Int): Result<Unit> =
        AuthApi.apiJson(
            "POST", "/efetivo/previsto",
            "{\"cc\":${jsonStr(cc)},\"funcao\":${jsonStr(funcao)},\"qtd\":$qtd}"
        ).map { }

    suspend fun atualizarFuncionario(fid: String, regime: Int? = null, desligamento: String? = null): Result<Unit> {
        val partes = buildList {
            if (regime != null) add("\"regime\":$regime")
            if (desligamento != null) add("\"desligamento\":${jsonStr(desligamento)}")
        }
        return AuthApi.apiJson("PUT", "/efetivo/funcionarios/${enc(fid)}", "{${partes.joinToString(",")}}").map { }
    }

    suspend fun custos(): Result<List<CustoMes>> =
        AuthApi.apiJson("GET", "/efetivo/custos").mapCatching { text ->
            objs(JsonMap.parse(text), "custos").map { c ->
                CustoMes(
                    comp = c.str("comp"),
                    versao = (c.long("versao") ?: 1L).toInt(),
                    valores = c.obj("valores")?.stringValues().orEmpty().mapNotNull { (k, v) -> v.toDoubleOrNull()?.let { k to it.toLong() } }.toMap(),
                    total = c.long("total") ?: 0L,
                    importadoPor = c.str("importadoPor"),
                    em = c.long("em") ?: 0L,
                    naoCasadas = c.strList("naoCasadas")
                )
            }
        }

    suspend fun importarFolha(imp: ImportacaoFolha): Result<Int> =
        AuthApi.apiJson(
            "POST", "/efetivo/custos",
            "{\"comp\":${jsonStr(imp.comp)},\"linhas\":[${imp.ok.joinToString(",") { "{\"fid\":${jsonStr(it.first)},\"cents\":${it.second}}" }}]," +
                "\"naoCasadas\":[${imp.naoCasadas.joinToString(",") { jsonStr(it.second) }}]}"
        ).mapCatching { (JsonMap.parse(it).long("versao") ?: 1L).toInt() }

    private fun transfFrom(t: JsonMap): Transferencia {
        val aprov = t.obj("aprov")
        fun lado(k: String): AprovLado {
            val l = aprov?.obj(k)
            return AprovLado(
                obra = l?.str("obra").orEmpty(),
                dec = l?.str("dec")?.ifBlank { null } ?: "pendente",
                por = l?.str("por").orEmpty(),
                porNome = l?.str("porNome").orEmpty(),
                em = l?.long("em"),
                auto = l?.bool("auto") == true,
                com = l?.str("com").orEmpty()
            )
        }
        return Transferencia(
            id = t.str("id"),
            fid = t.str("fid"),
            origem = t.str("origem"),
            destino = t.str("destino"),
            ini = t.str("ini"),
            fim = t.str("fim").ifBlank { null },
            motivo = t.str("motivo"),
            solicitante = t.str("solicitante"),
            solicitanteNome = t.str("solicitanteNome"),
            status = t.str("status"),
            criada = t.long("criada") ?: 0L,
            prazo = t.long("prazo") ?: 0L,
            avisoDiretoria = t.long("avisoDiretoria"),
            aplicadaEm = t.long("aplicadaEm"),
            origemLado = lado("origem"),
            destinoLado = lado("destino")
        )
    }

    private fun objs(o: JsonMap, k: String): List<JsonMap> = o.array(k).orEmpty().mapNotNull { it as? JsonMap }

    private fun jsonStr(s: String): String = buildString {
        append('"')
        for (c in s) {
            when (c) {
                '\\' -> append("\\\\")
                '"' -> append("\\\"")
                '\n' -> append("\\n")
                '\r' -> append("\\r")
                '\t' -> append("\\t")
                else -> if (c.code < 0x20) append("\\u" + c.code.toString(16).padStart(4, '0')) else append(c)
            }
        }
        append('"')
    }

    private fun enc(s: String): String = buildString {
        for (b in s.encodeToByteArray()) {
            val c = b.toInt().toChar()
            if (c.isLetterOrDigit() && b >= 0 || c == '-' || c == '_' || c == '.' || c == '~') append(c)
            else append('%').append(((b.toInt() and 0xFF).toString(16).uppercase()).padStart(2, '0'))
        }
    }
}
